/**
 * SharePopupV2 API 层
 * 需求: 2.2
 *
 * 提供分享组件所需的 API 接口：
 * - getUserInfo: 获取用户信息（包含邀请码）
 * - getShareDomain: 获取分享域名
 * - generateMiniProgramLink: 生成小程序链接
 * - getUnlimitedQRCode: 生成小程序二维码
 */

import { http } from '@/utils/http'
import type { UserInfo, MiniProgramParams, PromotionFingerprintResponse } from '../types/share'

// ============================================
// API 响应类型定义
// ============================================

/** 用户信息 API 响应数据 */
interface UserInfoData {
  inviteCode: string
  avatar?: string
  nickname?: string
  phoneNum?: string
  [key: string]: unknown
}

/** 分享域名 API 响应数据 */
interface ShareDomainData {
  sysContent: string
}

/** 小程序链接 API 响应数据 */
interface MiniProgramLinkData {
  url_link: string
}

/** 二维码 API 响应数据 */
interface QRCodeData {
  url: string
}

// ============================================
// API 接口实现
// ============================================

/**
 * 获取用户信息（包含邀请码）
 * @returns Promise<UserInfo> 用户信息
 */
export const getUserInfo = async (): Promise<UserInfo> => {
  const res = await http.post<UserInfoData>(
    '/nxcloud-app-user/c/app-c/v1/auth/getCurrentUserInfo',
    {}
  )
  return {
    inviteCode: res.data.inviteCode || '',
    avatar: res.data.avatar,
    nickname: res.data.nickname,
    phoneNum: res.data.phoneNum,
  }
}

/**
 * 获取分享域名
 * @returns Promise<string> 分享域名
 */
export const getShareDomain = async (): Promise<string> => {
  const res = await http.get<ShareDomainData>(
    '/nxcloud-app-user/c/app-c/v1/systemConfigApi/getConfigByKey/SHARE_DOMAIN'
  )
  return res.data.sysContent || ''
}

/**
 * 生成小程序链接
 * @param params 小程序参数
 * @returns Promise<string> 小程序链接
 */
export const generateMiniProgramLink = async (
  params: MiniProgramParams
): Promise<string> => {
  const { page, scene = '', env_version = 'release' } = params

  // 构建 query 参数
  const query = scene ? `scene=${encodeURIComponent(scene)}` : ''

  const res = await http.post<MiniProgramLinkData>(
    '/nxcloud-base-wechat/wx/ma/code/LAND-C/10000/generateUrlLink',
    {},
    {
      path: page,
      query,
      env_version,
    }
  )
  return res.data.url_link || ''
}

/**
 * 生成小程序二维码
 * @param params 小程序参数
 * @returns Promise<string> 二维码图片 URL
 */
export const getUnlimitedQRCode = async (
  params: MiniProgramParams
): Promise<string> => {
  const { page, scene = '' } = params

  const res = await http.get<QRCodeData>(
    '/nxcloud-base-wechat/wx/ma/code/LAND-C/10000/getUnlimitedQRCode',
    {
      page,
      scene
    }
  )
  return res.data.url || ''
}

/**
 * 上报分享次数（可选）
 * @param contentId 内容 ID
 * @returns Promise<void>
 */
export const reportShareCount = async (contentId: string): Promise<void> => {
  await http.post('/nxcloud-app-feed/c/mini-c/v1/feedContent/shareCount', {
    code: contentId,
  })
}

/**
 * 预生成指纹码响应类型
 * 需求: 7.1
 */

/**
 * 预生成推广指纹码（用于朋友圈分享）
 * 需求: 7.1
 *
 * 由于微信小程序 onShareTimeline 不支持异步返回，
 * 需要在初始化时预生成指纹码，供朋友圈分享同步使用
 *
 * @returns Promise<string> 预生成的推广指纹码
 */
export const generatePFCodeApi = async (): Promise<string> => {
  const res = await http.post<any>(
    '/nxcloud-app-dsp/c/app-c/v1/CPromotionFingerprintInfo/generatePFCode',
    {}
  )
  return res.data
}

/**
 * 创建推广指纹码
 * 需求: 10.2, 7.3
 * @param params 推广参数
 * @returns Promise<PromotionFingerprintResponse> 推广指纹码响应
 */
export interface CreateFingerprintParams {
  /** 推广参数 JSON 字符串 */
  promotionParamJson: string
  /** 推广业务类型 */
  promotionBizType: string
  /** 预生成的推广指纹码（可选，朋友圈分享时传入）- 需求: 7.3 */
  promotionFingerprintCode?: string
}

export const createFingerprintCodeApi = async (
  params: CreateFingerprintParams
): Promise<PromotionFingerprintResponse> => {
  const res = await http.post<PromotionFingerprintResponse>(
    '/nxcloud-app-dsp/c/app-c/v1/CPromotionFingerprintInfo/save',
    params
  )
  return res.data
}

/**
 * 获取推广指纹码详情
 * 用于解析 scene 参数还原业务数据
 * @param promotionFingerprintCode 推广指纹码
 * @returns Promise<PromotionFingerprintResponse> 指纹详情
 */
export const getFingerprintDetailApi = async (
  promotionFingerprintCode: string
): Promise<PromotionFingerprintResponse> => {
  const res = await http.post<PromotionFingerprintResponse>(
    '/nxcloud-app-dsp/c/app-c/v1/CPromotionFingerprintInfo/getDetail',
    { promotionFingerprintCode }
  )
  return res.data
}
